import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent,
  Chip, CircularProgress, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup,
  Snackbar, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { AlertCircle, Check, ChevronDown, GitBranch, Key, Plus, Rocket, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { encryptString, fetchEntitlements } from '@/lib/crypto';
import { GitAccessDialog, ProjectMappingsEditor, extractBaseUrl } from './components';
import type { DataSourceItem } from './components';

export default function OnboardingPage() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { refreshProject } = useProjectName();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [completing, setCompleting] = useState(false);
  const [gitRepos, setGitRepos] = useState<DataSourceItem[]>([]);
  const [url, setUrl] = useState('');
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);
  const [accessDialog, setAccessDialog] = useState<{ open: boolean; validating: boolean; error: string }>({ open: false, validating: false, error: '' });
  const [gitAuthMode, setGitAuthMode] = useState<'pid' | 'pat'>('pid');
  const [gitPat, setGitPat] = useState('');
  const [gitPatSaving, setGitPatSaving] = useState(false);
  const [gitPatConfigured, setGitPatConfigured] = useState(false);
  const [sonarConfigured, setSonarConfigured] = useState(false);
  const [sonarUrl, setSonarUrl] = useState('');
  const [sonarToken, setSonarToken] = useState('');
  const [sonarOrg, setSonarOrg] = useState('');
  const [sonarScope] = useState<'shared'>('shared');
  const [sonarMappings, setSonarMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [sonarValidating, setSonarValidating] = useState(false);
  const [sonarError, setSonarError] = useState('');
  const [bdConfigured, setBdConfigured] = useState(false);
  const [bdUrl, setBdUrl] = useState('');
  const [bdToken, setBdToken] = useState('');
  const [bdScope] = useState<'shared'>('shared');
  const [bdMappings, setBdMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [bdValidating, setBdValidating] = useState(false);
  const [bdError, setBdError] = useState('');

  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const [dsRes, tokenRes] = await Promise.all([
          sentinelNetwork.get(`/config/project/${appId}/data-sources`),
          sentinelNetwork.get(`/config/project/${appId}/tokens`),
        ]);
        setGitRepos((dsRes.data?.data || []).filter((d: any) => d.platform === 'Git'));
        const tokens = tokenRes.data?.data || [];
        if (tokens.find((t: any) => t.platform === 'Git')) { setGitAuthMode('pat'); setGitPatConfigured(true); }
        if (tokens.find((t: any) => t.platform === 'SonarQube')) setSonarConfigured(true);
        if (tokens.find((t: any) => t.platform === 'BlackDuck')) setBdConfigured(true);
      } catch {}
    })();
  }, [appId]);

  const validateGitUrl = (v: string) => /^https:\/\/.*\.git$/.test(v.trim());
  const gitRepoNames = gitRepos.map(r => { const p = r.url.replace('.git', '').split('/'); return p[p.length - 1]; });

  const doAddGitDataSource = async () => {
    setSaving(true);
    try {
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, { platform: 'Git', url: url.trim(), branch: branch.trim() || 'main' });
      const dsRes = await sentinelNetwork.get(`/config/project/${appId}/data-sources`);
      setGitRepos((dsRes.data?.data || []).filter((d: any) => d.platform === 'Git'));
      setUrl(''); setBranch('');
      setSnackbar({ open: true, message: 'Repository added', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to add repository', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleAddGit = () => {
    if (!validateGitUrl(url)) { setSnackbar({ open: true, message: 'Invalid Git URL. Must be HTTPS and end with .git', severity: 'error' }); return; }
    setAccessDialog({ open: true, validating: false, error: '' });
  };

  const handleAccessConfirm = async () => {
    setAccessDialog(prev => ({ ...prev, validating: true, error: '' }));
    try {
      const res = await sentinelNetwork.post('/validate-git-access', { gitUrl: url, branches: [branch || 'main'] });
      if (res.data.success) { setAccessDialog({ open: false, validating: false, error: '' }); await doAddGitDataSource(); }
      else setAccessDialog(prev => ({ ...prev, validating: false, error: res.data.message || 'Access validation failed.' }));
    } catch { setAccessDialog({ open: false, validating: false, error: '' }); await doAddGitDataSource(); }
  };

  const handleDeleteGit = async (item: DataSourceItem) => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/data-sources`, { data: { url: item.url } });
      setGitRepos(prev => prev.filter(r => r.url !== item.url));
    } catch { setSnackbar({ open: true, message: 'Failed to remove', severity: 'error' }); }
  };

  const handleSaveGitPat = async () => {
    setGitPatSaving(true);
    try {
      const { publicKey } = await fetchEntitlements();
      const enc = encryptString(gitPat, publicKey);
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, { platform: 'Git', encryptedConfig: enc, url: 'git-pat', scope: 'shared' });
      setGitPat(''); setGitPatConfigured(true);
      setSnackbar({ open: true, message: 'Git PAT saved', severity: 'success' });
    } catch { setSnackbar({ open: true, message: 'Failed to save PAT', severity: 'error' }); }
    finally { setGitPatSaving(false); }
  };

  const handleDeleteGitPat = async () => {
    try { await sentinelNetwork.delete(`/config/project/${appId}/tokens`, { data: { platform: 'Git' } }); setGitPatConfigured(false); }
    catch { setSnackbar({ open: true, message: 'Failed to remove PAT', severity: 'error' }); }
  };

  const handleSonarVerify = async () => {
    setSonarValidating(true); setSonarError('');
    try {
      const { publicKey } = await fetchEntitlements();
      const encryptedToken = encryptString(sonarToken, publicKey);
      const res = await sentinelNetwork.post('/validate-sonarqube-access', { url: sonarUrl.trim(), encryptedToken, organization: sonarOrg });
      if (!res.data.success) { setSonarError(res.data.message || 'Validation failed'); return; }
      const base = extractBaseUrl(sonarUrl);
      const enc = encryptString(JSON.stringify({ baseUrl: base, token: sonarToken, organization: sonarOrg, platform: 'SonarQube', scope: sonarScope, projectMappings: sonarMappings }), publicKey);
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, { platform: 'SonarQube', encryptedConfig: enc, url: base, organization: sonarOrg, mappings: sonarMappings, scope: sonarScope });
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, { platform: 'SonarQube', url: base });
      setSonarConfigured(true); setSonarUrl(''); setSonarToken(''); setSonarOrg('');
      setSnackbar({ open: true, message: 'SonarQube configured', severity: 'success' });
    } catch (err: any) { setSonarError(err?.response?.data?.message || 'Failed'); }
    finally { setSonarValidating(false); }
  };

  const handleBdVerify = async () => {
    setBdValidating(true); setBdError('');
    try {
      const base = extractBaseUrl(bdUrl);
      const { publicKey } = await fetchEntitlements();
      const encryptedToken = encryptString(bdToken, publicKey);
      const res = await sentinelNetwork.post('/validate-blackduck-access', { url: base, encryptedToken });
      if (!res.data.success) { setBdError(res.data.message || 'Validation failed'); return; }
      const enc = encryptString(JSON.stringify({ baseUrl: base, token: bdToken, platform: 'BlackDuck', scope: bdScope, projectMappings: bdMappings }), publicKey);
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, { platform: 'BlackDuck', encryptedConfig: enc, url: base, mappings: bdMappings, scope: bdScope });
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, { platform: 'BlackDuck', url: base });
      setBdConfigured(true); setBdUrl(''); setBdToken('');
      setSnackbar({ open: true, message: 'BlackDuck configured', severity: 'success' });
    } catch (err: any) { setBdError(err?.response?.data?.message || 'Failed'); }
    finally { setBdValidating(false); }
  };

  const handleCompleteSetup = async () => {
    setCompleting(true);
    try {
      await sentinelNetwork.post('/extract', { appId, scanType: 'complete' });
      await refreshProject(appId!);
      navigate(`/project/${appId}`, { replace: true });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to start scan', severity: 'error' });
      setCompleting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: 1.5 }}>
            <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <Rocket size={28} color={theme.palette.primary.main} />
            </Box>
          </Stack>
          <Typography variant="h5" fontWeight={700}>Set Up Your Project</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Add at least one Git repository to get started. Optional integrations can be configured now or later.
          </Typography>
        </Box>

        {/* MANDATORY: Git */}
        <Card elevation={0} sx={{ border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <GitBranch size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>Git Repositories</Typography>
              <Chip label="Required" size="small" color="primary" sx={{ fontSize: 11, height: 22 }} />
            </Stack>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Authentication Method</FormLabel>
              <RadioGroup row value={gitAuthMode} onChange={(_, v) => setGitAuthMode(v as 'pid' | 'pat')}>
                <FormControlLabel value="pid" control={<Radio size="small" />} label={<Typography variant="body2">Sentinel PID User Access</Typography>} />
                <FormControlLabel value="pat" control={<Radio size="small" />} label={<Typography variant="body2">Personal Access Token</Typography>} />
              </RadioGroup>
            </FormControl>
            {gitAuthMode === 'pid' ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>Grant <strong>Write access</strong> to PID user <strong>PIDF6B7</strong> on your Git repositories.</Alert>
            ) : (
              <Box sx={{ mb: 2 }}>
                {gitPatConfigured ? (
                  <Alert severity="success" icon={<Check size={18} />} action={<Button size="small" color="error" onClick={handleDeleteGitPat}>Remove</Button>}>Git PAT configured</Alert>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField size="small" label="Personal Access Token" type="password" value={gitPat} onChange={e => setGitPat(e.target.value)} sx={{ flex: 1 }} />
                    <Button variant="contained" size="small" disabled={!gitPat.trim() || gitPatSaving} onClick={handleSaveGitPat}
                      startIcon={gitPatSaving ? <CircularProgress size={16} color="inherit" /> : <Key size={16} />}>{gitPatSaving ? 'Saving...' : 'Save PAT'}</Button>
                  </Stack>
                )}
              </Box>
            )}
            <Grid container spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
              <Grid item xs={6}><TextField size="small" fullWidth label="Repository URL" placeholder="https://github.com/org/repo.git" value={url} onChange={e => setUrl(e.target.value)} /></Grid>
              <Grid item xs={3}><TextField size="small" fullWidth label="Branch" placeholder="main" value={branch} onChange={e => setBranch(e.target.value)} /></Grid>
              <Grid item xs={3}><Button fullWidth variant="contained" disabled={!url.trim() || saving} onClick={handleAddGit} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Plus size={16} />} sx={{ height: 40 }}>Add</Button></Grid>
            </Grid>
            {gitRepos.length > 0 ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                {gitRepos.map((repo, i) => (
                  <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, borderBottom: i < gitRepos.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Stack><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{repo.url}</Typography><Typography variant="caption" color="text.secondary">Branch: {repo.branch || 'main'}</Typography></Stack>
                    <Button size="small" color="error" onClick={() => handleDeleteGit(repo)} startIcon={<Trash2 size={14} />}>Remove</Button>
                  </Stack>
                ))}
              </Box>
            ) : (<Alert severity="warning" icon={<AlertCircle size={18} />} sx={{ fontSize: 12 }}>No repositories added yet. Add at least one to continue.</Alert>)}
          </CardContent>
        </Card>

        <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>Optional Add-ons</Typography>

        <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>SonarQube</Typography>
              {sonarConfigured && <Chip icon={<Check size={12} />} label="Configured" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {sonarConfigured ? (
              <Alert severity="success">SonarQube integration is configured. You can modify it later in Data Sources settings.</Alert>
            ) : (
              <Stack spacing={1.5}>
                <TextField size="small" fullWidth label="SonarQube URL" placeholder="https://sonarcloud.io or self-hosted URL" value={sonarUrl} onChange={e => setSonarUrl(e.target.value)} />
                <TextField size="small" fullWidth label="Organization" value={sonarOrg} onChange={e => setSonarOrg(e.target.value)} />
                <TextField size="small" fullWidth label="Token" type="password" value={sonarToken} onChange={e => setSonarToken(e.target.value)} />
                <ProjectMappingsEditor mappings={sonarMappings} onChange={setSonarMappings} label="Project Mappings (optional)" projectKeyLabel="SonarQube project key" gitRepoNames={gitRepoNames} />
                {sonarError && <Alert severity="error" sx={{ fontSize: 12 }}>{sonarError}</Alert>}
                <Button variant="outlined" disabled={!sonarUrl || !sonarToken || sonarValidating} onClick={handleSonarVerify}
                  startIcon={sonarValidating ? <CircularProgress size={16} /> : <Check size={16} />}>
                  {sonarValidating ? 'Validating...' : 'Verify & Save'}
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>BlackDuck</Typography>
              {bdConfigured && <Chip icon={<Check size={12} />} label="Configured" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {bdConfigured ? (
              <Alert severity="success">BlackDuck integration is configured. You can modify it later in Data Sources settings.</Alert>
            ) : (
              <Stack spacing={1.5}>
                <TextField size="small" fullWidth label="BlackDuck URL" placeholder="https://blackduck.example.com" value={bdUrl} onChange={e => setBdUrl(e.target.value)} />
                <TextField size="small" fullWidth label="API Token" type="password" value={bdToken} onChange={e => setBdToken(e.target.value)} />
                <ProjectMappingsEditor mappings={bdMappings} onChange={setBdMappings} label="Project Mappings (optional)" projectKeyLabel="BlackDuck project name" gitRepoNames={gitRepoNames} />
                {bdError && <Alert severity="error" sx={{ fontSize: 12 }}>{bdError}</Alert>}
                <Button variant="outlined" disabled={!bdUrl || !bdToken || bdValidating} onClick={handleBdVerify}
                  startIcon={bdValidating ? <CircularProgress size={16} /> : <Check size={16} />}>
                  {bdValidating ? 'Validating...' : 'Verify & Save'}
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Complete Setup */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 3 }}>
          <Button variant="contained" size="large" disabled={gitRepos.length === 0 || completing} onClick={handleCompleteSetup}
            startIcon={completing ? <CircularProgress size={20} color="inherit" /> : <Rocket size={20} />}
            sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 16 }}>
            {completing ? 'Starting Scan...' : 'Complete Setup & Start Scan'}
          </Button>
        </Box>
      </Stack>

      <GitAccessDialog open={accessDialog.open} validating={accessDialog.validating} error={accessDialog.error}
        url={url} onConfirm={handleAccessConfirm} onClose={() => setAccessDialog({ open: false, validating: false, error: '' })} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
